// /////////////////////////////////////////////////////////////////////
//
//
// (C) Copyright 2021 Autodesk, Inc. All rights reserved.
//
//                     ****  CONFIDENTIAL MATERIAL  ****
//
// The information contained herein is confidential, proprietary to
// Autodesk, Inc., and considered a trade secret.  Use of this information
// by anyone other than authorized employees of Autodesk, Inc. is granted
// only under a written nondisclosure agreement, expressly prescribing the
// the scope and manner of such use.
//
// /////////////////////////////////////////////////////////////////////

import {
  Project, ProjectRevitVersionDetectionResult,
  StartProjectRevitVersionDetectionRequest
} from "../clients/Classes";
import {ClientProvider} from "../clients/ClientProvider";
import {ProjectUI} from "../dataModel/ProjectUI";
import {ProjectTranslator} from "../dataModel/Translators/ProjectTranslator";
import {ServiceBase} from "./ServiceBase";
import {IV2Client} from "../clients/V2Client";
import {ApiException, IClient} from "../clients/Client";
import {AxiosError} from "axios";

export class ProjectService extends ServiceBase {
  private _client: IClient = ClientProvider.Client;
  private _v2Client: IV2Client = ClientProvider.V2Client;

  GetProjects(): Promise<ProjectUI[]> {
    return this.TryPromiseWithCatch(() =>
      this._client.listHubs(true)
        .then(async (hubs) => {
          const promises: Promise<Project[]>[] = [];

          hubs.forEach(h => {
            promises.push(
              this.GetRemainingDataFromPaginatedEndpoint(t =>
                this._v2Client.listHubProjects(h.id!, t))
                .then(r => r.items!)
            );
          });

          const allProjects: ProjectUI[] = [];
          await Promise.all(promises)
            .then(projectSets => {
              projectSets.forEach(ps => ps
                .map(p => ProjectTranslator.TranslateProject(p))
                .forEach(p => allProjects.push(p)))
            });
          return allProjects;
        })
    );
  }

  GetProject(hubId: string, id: string): Promise<ProjectUI> {
    return this.TryPromiseWithCatch(() =>
      this._client.getProject(hubId, id)
        .then(project => ProjectTranslator.TranslateProject(project))
    );
  }

  GetProjectRevitVersion(hubId: string, projectId: string): Promise<ProjectRevitVersionDetectionResult> {
    return this.TryPromiseWithCatch(() =>
      this._client.startProjectRvtVersionDetection(new StartProjectRevitVersionDetectionRequest({
        hubId: hubId,
        projectId: projectId
      }))
        .then(response =>
          this.WaitForItemReady(response, 'isDone', () => this._client.getProjectRvtVersionDetection(response.id!))
            .catch(error => {
                if ((error instanceof ApiException && error.status === 404)
                  || (error instanceof AxiosError && error.isAxiosError && error.response?.status === 404)) {
                  return new ProjectRevitVersionDetectionResult({
                    id: response.id,
                    revitProjectVersion: undefined,
                    projectId: projectId,
                    isDone: true,
                    hubId: hubId,
                    searchMaxPages: true,
                    errorMessage: 'Request timed out'
                  })
                } else {
                  throw error;
                }
              }
            )
        )
    );
  }
}